/**
 * Background Sync Client Service
 *
 * Provides a client-side interface to the service worker's background sync
 * capabilities. Allows the app to:
 * - Check if items are pending sync
 * - Receive notifications when sync completes
 * - Request immediate sync attempts
 *
 * Usage:
 * ```ts
 * import { BackgroundSyncService } from '@/services/sync/background-sync';
 *
 * const syncService = BackgroundSyncService.getInstance();
 *
 * // Listen for sync completions
 * syncService.onSyncComplete((tag) => {
 *   console.log('Sync completed:', tag);
 *   refreshData();
 * });
 *
 * // Check queue status
 * const status = await syncService.getQueueStatus();
 * if (status.entries > 0) {
 *   showPendingSyncBadge(status.entries);
 * }
 * ```
 */

export interface QueueStatus {
  entries: number;
  cgm: number;
  support: number;
  settings: number;
}

export interface SyncEvent {
  type: "SYNC_COMPLETE" | "SYNC_FAILED" | "QUEUE_STATUS";
  tag?: string;
  error?: string;
  pendingCount?: number;
}

type SyncCompleteHandler = (tag: string) => void;
type SyncFailedHandler = (tag: string, error: string) => void;
type QueueChangeHandler = (status: QueueStatus) => void;

/**
 * Client-side service for interacting with Background Sync.
 */
export class BackgroundSyncService {
  private static instance: BackgroundSyncService | null = null;

  private syncCompleteHandlers: Set<SyncCompleteHandler> = new Set();
  private syncFailedHandlers: Set<SyncFailedHandler> = new Set();
  private queueChangeHandlers: Set<QueueChangeHandler> = new Set();

  private constructor() {
    this.setupMessageListener();
  }

  /**
   * Get the singleton instance.
   */
  static getInstance(): BackgroundSyncService {
    if (!BackgroundSyncService.instance) {
      BackgroundSyncService.instance = new BackgroundSyncService();
    }
    return BackgroundSyncService.instance;
  }

  /**
   * Check if Background Sync is supported.
   */
  static isSupported(): boolean {
    return "serviceWorker" in navigator && "SyncManager" in window;
  }

  /**
   * Set up listener for messages from service worker.
   */
  private setupMessageListener(): void {
    if (!("serviceWorker" in navigator)) return;

    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data as SyncEvent;

      switch (data.type) {
        case "SYNC_COMPLETE":
          if (data.tag) {
            this.syncCompleteHandlers.forEach((h) => h(data.tag!));
          }
          break;

        case "SYNC_FAILED":
          if (data.tag && data.error) {
            this.syncFailedHandlers.forEach((h) => h(data.tag!, data.error!));
          }
          break;

        case "QUEUE_STATUS":
          // This comes as a response to GET_QUEUE_STATUS, handled via MessageChannel
          break;
      }
    });
  }

  /**
   * Register a handler for sync completion events.
   * Returns an unsubscribe function.
   */
  onSyncComplete(handler: SyncCompleteHandler): () => void {
    this.syncCompleteHandlers.add(handler);
    return () => this.syncCompleteHandlers.delete(handler);
  }

  /**
   * Register a handler for sync failure events.
   * Returns an unsubscribe function.
   */
  onSyncFailed(handler: SyncFailedHandler): () => void {
    this.syncFailedHandlers.add(handler);
    return () => this.syncFailedHandlers.delete(handler);
  }

  /**
   * Register a handler for queue status changes.
   * Returns an unsubscribe function.
   */
  onQueueChange(handler: QueueChangeHandler): () => void {
    this.queueChangeHandlers.add(handler);
    return () => this.queueChangeHandlers.delete(handler);
  }

  /**
   * Get the current queue status from the service worker.
   */
  async getQueueStatus(): Promise<QueueStatus> {
    if (!("serviceWorker" in navigator)) {
      return { entries: 0, cgm: 0, support: 0, settings: 0 };
    }

    const registration = await navigator.serviceWorker.ready;
    if (!registration.active) {
      return { entries: 0, cgm: 0, support: 0, settings: 0 };
    }

    return new Promise((resolve) => {
      const channel = new MessageChannel();

      channel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      // Timeout fallback
      const timeout = setTimeout(() => {
        resolve({ entries: 0, cgm: 0, support: 0, settings: 0 });
      }, 1000);

      channel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        resolve(event.data);
      };

      registration.active!.postMessage({ type: "GET_QUEUE_STATUS" }, [
        channel.port2,
      ]);
    });
  }

  /**
   * Request a one-time background sync.
   * This is useful for triggering sync when coming back online.
   */
  async requestSync(tag: string): Promise<boolean> {
    if (!BackgroundSyncService.isSupported()) {
      console.warn("[BackgroundSync] Not supported in this browser");
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register(tag);
      console.log(`[BackgroundSync] Registered sync: ${tag}`);
      return true;
    } catch (error) {
      console.error(`[BackgroundSync] Failed to register sync: ${tag}`, error);
      return false;
    }
  }

  /**
   * Request periodic background sync (where supported).
   * Allows the app to periodically sync even when not open.
   */
  async requestPeriodicSync(
    tag: string,
    minIntervalMs: number = 12 * 60 * 60 * 1000, // 12 hours
  ): Promise<boolean> {
    if (!("periodicSync" in ServiceWorkerRegistration.prototype)) {
      console.warn("[BackgroundSync] Periodic sync not supported");
      return false;
    }

    try {
      const status = await navigator.permissions.query({
        name: "periodic-background-sync" as PermissionName,
      });

      if (status.state !== "granted") {
        console.warn("[BackgroundSync] Periodic sync permission not granted");
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      await (registration as any).periodicSync.register(tag, {
        minInterval: minIntervalMs,
      });
      console.log(`[BackgroundSync] Registered periodic sync: ${tag}`);
      return true;
    } catch (error) {
      console.error("[BackgroundSync] Failed to register periodic sync", error);
      return false;
    }
  }

  /**
   * Get the total number of pending items across all queues.
   */
  async getTotalPendingCount(): Promise<number> {
    const status = await this.getQueueStatus();
    return status.entries + status.cgm + status.support + status.settings;
  }

  /**
   * Check if there are any pending items.
   */
  async hasPendingItems(): Promise<boolean> {
    return (await this.getTotalPendingCount()) > 0;
  }
}

// Export singleton getter for convenience
export function getBackgroundSyncService(): BackgroundSyncService {
  return BackgroundSyncService.getInstance();
}
